import { useCallback, useMemo, useState, type MouseEvent } from 'react'

import { useDispatch, useSelector } from 'react-redux'
import { browserHistory } from 'react-router'
import useSWR from 'swr'

import { menuFetcher, type MenuQueryInput } from '@library/api-menu-experience'
import type { CollectionSlug, CollectionUUID } from '@library/menu-service'
import { queueTrackingEvent } from '@library/tracking-integration'

import { actionTypes } from 'actions/actionTypes'
import { recipeCollectionSelected } from 'actions/trackingKeys'
import type {
  BrowseSelectionState,
  SelectBrowseEvent,
  SelectCollectionEvent,
} from 'routes/Menu/MenuExperience/types'
import { getIsMenuContextLoaded } from 'routes/Menu/selectors/menu'
import { getBasketDate, getNumPortions } from 'selectors/basket'

import type { MenuQueryParams } from '../../../context/menuQueryContext'
import { getMenuExperiencePreview } from '../../../context/previewContext'

type MenuExperienceQuery = MenuQueryParams & Record<string, unknown> & { collection?: string }

export type MenuExperienceCategorySelectionMethod =
  SelectCollectionEvent['properties']['navigation_method']

export type MenuExperienceMenuSWRKey = {
  key: 'menu-experience-menu'
  input: MenuQueryInput
}

export type MenuExperienceNavItem = {
  id: string
  text: string
  slug: string
  selected: boolean
  onClick: (event?: MouseEvent<HTMLElement>) => void
}

export type MenuExperienceCategoryNavigationTarget = {
  id: string
  slug: string
}

type CategoryAnalyticsProperties = Pick<SelectCollectionEvent['properties'], 'merch_header_type'>

export type MenuExperienceCategorySelect = (
  category: MenuExperienceCategoryNavigationTarget,
  navigationMethod: MenuExperienceCategorySelectionMethod,
  parentCategoryId?: string,
  analyticsProperties?: CategoryAnalyticsProperties,
) => void

const fetchMenuExperience = ({ input }: MenuExperienceMenuSWRKey) => menuFetcher(input)

const getSelectedCategorySlug = (query: MenuExperienceQuery) =>
  typeof query.collection === 'string' && query.collection.trim().length > 0
    ? query.collection
    : undefined

type MenuExperienceMenuSWRKeyInput = {
  deliveryDate?: string
  numPortions?: number
  query: MenuExperienceQuery
}

export const getMenuExperienceMenuSWRKey = ({
  deliveryDate,
  numPortions,
  query,
}: MenuExperienceMenuSWRKeyInput): MenuExperienceMenuSWRKey | null => {
  const preview = getMenuExperiencePreview(query)
  const selectedCategorySlug = getSelectedCategorySlug(query)

  return deliveryDate && numPortions
    ? {
        key: 'menu-experience-menu',
        input: {
          deliveryDate,
          numPortions,
          ...(selectedCategorySlug && { categorySlug: selectedCategorySlug }),
          ...(preview && { preview }),
        },
      }
    : null
}

export const useMenuExperienceNavigation = (query: MenuExperienceQuery, pathname = '/menu') => {
  const dispatch = useDispatch()
  const [browseSelectionState, setBrowseSelectionState] = useState<BrowseSelectionState>(null)
  const deliveryDate = useSelector(getBasketDate)
  const isMenuContextLoaded = useSelector(getIsMenuContextLoaded)
  const numPortions = useSelector(getNumPortions)
  const selectedCategorySlug = getSelectedCategorySlug(query)
  const menuSWRKey = isMenuContextLoaded
    ? getMenuExperienceMenuSWRKey({ deliveryDate, numPortions, query })
    : null

  const { data: menuData, error: menuError } = useSWR(menuSWRKey, fetchMenuExperience)

  const selectedNavSlug = selectedCategorySlug ?? menuData?.categorySlug

  const currentCategoryId = menuData?.categoryId
  const currentCategorySlug = menuData?.categorySlug
  const menuId = menuData?.menuId

  const clearBrowseSelectionState = useCallback(() => {
    setBrowseSelectionState(null)
  }, [])

  const selectCategory: MenuExperienceCategorySelect = useCallback(
    (category, navigationMethod, parentCategoryId, analyticsProperties) => {
      if (navigationMethod === 'browse') {
        setBrowseSelectionState(
          parentCategoryId
            ? {
                childCollectionId: category.id as CollectionUUID,
                parentCollectionId: parentCategoryId as CollectionUUID,
              }
            : null,
        )
      }

      if (category.slug === 'browse') {
        if (menuId) {
          queueTrackingEvent<SelectBrowseEvent>({
            action: 'select_browse',
            properties: {
              menu_uuid: menuId,
            },
          })
        }
      } else {
        queueTrackingEvent<SelectCollectionEvent>({
          action: 'select_collection',
          properties: {
            collection_id: category.id as CollectionUUID,
            collection_slug: category.slug as CollectionSlug,
            ...(currentCategoryId &&
              currentCategorySlug && {
                current_collection_id: currentCategoryId as CollectionUUID,
                current_collection_slug: currentCategorySlug as CollectionSlug,
              }),
            ...(parentCategoryId && { parent_collection_id: parentCategoryId as CollectionUUID }),
            ...analyticsProperties,
            navigation_method: navigationMethod,
          },
        })

        if (category.id !== currentCategoryId) {
          dispatch({
            type: actionTypes.TRACKING,
            trackingData: {
              actionType: recipeCollectionSelected,
              collectionId: category.id,
              fromCollectionId: currentCategoryId ?? null,
            },
          })
        }
      }

      browserHistory.push({
        pathname,
        query: {
          ...query,
          collection: category.slug,
        },
      })
    },
    [currentCategoryId, currentCategorySlug, dispatch, menuId, pathname, query],
  )

  const links = useMemo<MenuExperienceNavItem[]>(
    () =>
      (menuData?.categories ?? []).map((category) => ({
        id: category.id,
        text: category.title,
        slug: category.slug,
        selected: category.slug === selectedNavSlug,
        onClick: (event) => {
          event?.preventDefault()
          selectCategory(category, 'navigation_bar')
        },
      })),
    [menuData?.categories, selectCategory, selectedNavSlug],
  )

  return {
    data: menuData,
    browseSelectionState,
    clearBrowseSelectionState,
    deliveryDate,
    error: !menuData ? menuError : undefined,
    selectCategory,
    links,
  }
}
